// =============== 🧠 AI QUESTION ENHANCER ===============
// Adds AI metadata to existing questions without modifying original structure

// Feature flags
const FEATURE_FLAGS = {
  AI_SELECTION: import.meta.env?.VITE_AI_SELECTION === 'true' || true,
  ADVANCED_SCORING: import.meta.env?.VITE_ADVANCED_SCORING === 'true' || true
};

// =============== DIFFICULTY CALCULATION ===============
export const calculateDifficulty = (question) => {
  let difficultyScore = 0.5; // Default medium
  
  // Text complexity analysis
  const textLength = question.text.length;
  const wordCount = question.text.split(' ').length;
  const avgWordLength = question.text.replace(/[^a-zA-Z]/g, '').length / wordCount;
  
  // Complexity indicators
  if (textLength > 150) difficultyScore += 0.1;
  if (wordCount > 20) difficultyScore += 0.1;
  if (avgWordLength > 6) difficultyScore += 0.1;
  
  // Question type difficulty
  switch (question.type) {
    case 'likert':
      difficultyScore += 0.0; // Base difficulty
      break;
    case 'mcq':
      difficultyScore += question.options?.length > 4 ? 0.2 : 0.1;
      break;
    case 'choice':
      difficultyScore += 0.1;
      break;
    case 'text':
    case 'video':
      difficultyScore += 0.3; // Higher cognitive load
      break;
    default:
      difficultyScore += 0.1;
  }
  
  // Content complexity keywords
  const complexKeywords = ['analyze', 'evaluate', 'synthesize', 'compare', 'contrast', 'justify'];
  const hasComplexKeywords = complexKeywords.some(keyword => 
    question.text.toLowerCase().includes(keyword)
  );
  if (hasComplexKeywords) difficultyScore += 0.2;
  
  // Normalize to 0.1-0.9 range
  difficultyScore = Math.max(0.1, Math.min(0.9, difficultyScore));
  
  // Convert to difficulty levels
  if (difficultyScore <= 0.3) return 'easy';
  if (difficultyScore <= 0.6) return 'medium';
  return 'hard';
};

// =============== ADAPTIVE WEIGHTS GENERATION ===============
export const generateAdaptiveWeights = (question) => {
  const baseWeights = question.weights || {};
  
  // Generate performance-based weight adjustments
  const adaptiveWeights = {
    ...baseWeights,
    performanceMultipliers: {
      high: 1.2,    // Boost weight for high performers
      medium: 1.0,  // Standard weight
      low: 0.8      // Reduce weight for struggling users
    },
    contextualFactors: {
      timeOfDay: {
        morning: 1.1,   // Higher accuracy in morning
        afternoon: 1.0,
        evening: 0.9
      },
      sessionLength: {
        early: 1.1,     // Fresh attention
        middle: 1.0,
        late: 0.9       // Fatigue factor
      }
    }
  };
  
  return adaptiveWeights;
};

// =============== FOLLOW-UP GENERATION ===============
export const generateFollowUps = (question) => {
  const baseId = question.id;
  const category = question.category || 'general';
  const trait = question.trait || question.tags?.[0] || 'general';
  
  const followUps = {
    // Triggered by low scores (1-2 on likert, poor performance)
    low: {
      id: `${baseId}_probe_low`,
      text: generateLowProbeText(question, trait),
      type: 'text',
      category,
      tags: [...(question.tags || []), 'probe', 'low_performance'],
      parentId: baseId,
      triggerCondition: 'low'
    },
    
    // Triggered by high scores (4-5 on likert, excellent performance)
    high: {
      id: `${baseId}_probe_high`,
      text: generateHighProbeText(question, trait),
      type: 'text',
      category,
      tags: [...(question.tags || []), 'probe', 'high_performance'],
      parentId: baseId,
      triggerCondition: 'high'
    },
    
    // Triggered by neutral scores (3 on likert, average performance)
    mid: {
      id: `${baseId}_develop`,
      text: generateDevelopmentText(question, trait),
      type: 'text',
      category,
      tags: [...(question.tags || []), 'development', 'neutral'],
      parentId: baseId,
      triggerCondition: 'neutral'
    }
  };
  
  return followUps;
};

// =============== FOLLOW-UP TEXT GENERATORS ===============
const generateLowProbeText = (question, trait) => {
  const probeTemplates = {
    openness: "What specific situations make you uncomfortable with new experiences?",
    conscientiousness: "What challenges do you face when trying to stay organized?",
    extraversion: "What makes group interactions feel draining for you?",
    agreeableness: "When do you find it hardest to work with others?",
    neuroticism: "What physical symptoms do you notice when you feel stressed?",
    self_awareness: "What makes it difficult to recognize your emotions in the moment?",
    self_regulation: "What triggers make it hardest for you to stay calm?",
    motivation: "What factors reduce your motivation at work?",
    empathy: "When do you struggle to understand others' perspectives?",
    social_skills: "What communication situations feel most challenging?",
    general: "What specific aspects of this area would you like to develop?"
  };
  
  return probeTemplates[trait] || probeTemplates.general;
};

const generateHighProbeText = (question, trait) => {
  const successTemplates = {
    openness: "Describe a time when your openness to new ideas led to success.",
    conscientiousness: "How has your attention to detail helped you achieve goals?",
    extraversion: "Tell me about a situation where your social energy benefited a team.",
    agreeableness: "Describe how your collaborative nature has resolved conflicts.",
    neuroticism: "What strategies help you maintain calm under pressure?",
    self_awareness: "How do you use emotional awareness to improve your performance?",
    self_regulation: "Describe a time when emotional control helped you succeed.",
    motivation: "What drives your highest levels of performance?",
    empathy: "How has understanding others' emotions helped in your work?",
    social_skills: "Describe your most effective communication strategy.",
    general: "How has this strength contributed to your professional success?"
  };
  
  return successTemplates[trait] || successTemplates.general;
};

const generateDevelopmentText = (question, trait) => {
  const developmentTemplates = {
    openness: "What would help you become more comfortable with change?",
    conscientiousness: "What systems could help you improve your organization?",
    extraversion: "How could you balance social energy with focused work?",
    agreeableness: "How might you balance cooperation with assertiveness?",
    neuroticism: "What techniques could help you build stress resilience?",
    self_awareness: "What practices could improve your emotional awareness?",
    self_regulation: "What strategies would help you manage emotions better?",
    motivation: "What changes would increase your work engagement?",
    empathy: "How could you better understand others' perspectives?",
    social_skills: "What communication skills would you like to develop?",
    general: "What development approach would be most helpful for you?"
  };
  
  return developmentTemplates[trait] || developmentTemplates.general;
};

// =============== AI METADATA GENERATION ===============
export const generateAIMetadata = (question) => {
  return {
    version: '1.0',
    enhanced: true,
    enhancementDate: new Date().toISOString(),
    cognitiveLoad: calculateCognitiveLoad(question),
    semanticTags: extractSemanticTags(question),
    adaptiveParameters: {
      minResponseTime: question.type === 'text' ? 30 : 5, // seconds
      maxResponseTime: question.type === 'text' ? 300 : 60,
      optimalResponseTime: question.type === 'text' ? 90 : 15
    },
    learningObjectives: generateLearningObjectives(question)
  };
};

const calculateCognitiveLoad = (question) => {
  let load = 1; // Base load
  
  if (question.type === 'mcq' && question.options?.length > 4) load += 0.5;
  if (question.type === 'text' || question.type === 'video') load += 1;
  if (question.text.length > 100) load += 0.3;
  
  return Math.min(3, load); // Cap at 3
};

const extractSemanticTags = (question) => {
  const text = question.text.toLowerCase();
  const semanticTags = [];
  
  // Behavioral indicators
  if (text.includes('team') || text.includes('colleague')) semanticTags.push('teamwork');
  if (text.includes('stress') || text.includes('pressure')) semanticTags.push('stress_management');
  if (text.includes('decision') || text.includes('choose')) semanticTags.push('decision_making');
  if (text.includes('conflict') || text.includes('disagree')) semanticTags.push('conflict_resolution');
  if (text.includes('communicate') || text.includes('explain')) semanticTags.push('communication');
  
  return semanticTags;
};

const generateLearningObjectives = (question) => {
  const category = question.category;
  const objectives = {
    personality: ['Understand behavioral tendencies', 'Recognize trait patterns'],
    cognitive: ['Assess reasoning ability', 'Evaluate problem-solving skills'],
    emotional_intelligence: ['Measure emotional awareness', 'Assess social skills'],
    situational_judgement: ['Evaluate decision-making', 'Assess professional judgment'],
    motivation_values: ['Identify core drivers', 'Understand value alignment'],
    behavioral: ['Assess communication skills', 'Evaluate adaptability'],
    mindfulness: ['Measure stress resilience', 'Assess focus ability']
  };
  
  return objectives[category] || ['General assessment'];
};

// =============== MAIN ENHANCEMENT FUNCTION ===============
export const enhanceQuestionWithAI = (question) => {
  // Return original question if AI features disabled
  if (!FEATURE_FLAGS.AI_SELECTION) {
    return question;
  }
  
  // Add AI enhancements while preserving original structure
  return {
    ...question, // Keep ALL original properties
    
    // AI enhancements
    difficulty: calculateDifficulty(question),
    followUpTriggers: generateFollowUps(question),
    adaptiveWeights: generateAdaptiveWeights(question),
    aiMetadata: generateAIMetadata(question)
  };
};

// =============== BATCH ENHANCEMENT ===============
export const enhanceQuestionBank = (questions) => {
  if (!FEATURE_FLAGS.AI_SELECTION) {
    return questions;
  }
  
  return questions.map(enhanceQuestionWithAI);
};

export const enhanceCategory = (category) => {
  if (!FEATURE_FLAGS.AI_SELECTION) {
    return category;
  }
  
  return {
    ...category,
    questions: enhanceQuestionBank(category.questions),
    aiEnabled: true,
    totalQuestionPool: category.questions.length * 4, // Base + 3 follow-ups each
    enhancementVersion: '1.0'
  };
};

// =============== EXPORTS ===============
export { FEATURE_FLAGS };
export default enhanceQuestionWithAI;